import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Lock, Download, RefreshCw, Wine } from "lucide-react";
import { adminVerify, adminListParticipations, adminStats, exportCsvUrl } from "@/api";

function formatDate(iso) {
  try {
    const d = new Date(iso);
    const p = (n) => String(n).padStart(2, "0");
    return `${p(d.getDate())}.${p(d.getMonth() + 1)} · ${p(d.getHours())}:${p(d.getMinutes())}`;
  } catch {
    return "";
  }
}

export default function Admin() {
  const navigate = useNavigate();
  const [pin, setPin] = useState("");
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [currentPin, setCurrentPin] = useState("");

  const refresh = useCallback(async (p) => {
    setLoading(true);
    setErr(null);
    try {
      const [list, st] = await Promise.all([adminListParticipations(p), adminStats(p)]);
      setItems(list);
      setTotal(st.total);
    } catch (e) {
      setErr(e?.message || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, []);

  const onLogin = async () => {
    if (pin.length < 4) {
      setErr("Code PIN trop court");
      return;
    }
    setLoading(true);
    setErr(null);
    const ok = await adminVerify(pin);
    if (!ok) {
      setErr("Code PIN invalide");
      setLoading(false);
      return;
    }
    setCurrentPin(pin);
    setAuthed(true);
  };

  useEffect(() => {
    if (authed && currentPin) refresh(currentPin);
  }, [authed, currentPin, refresh]);

  const onExport = () => {
    window.open(`${exportCsvUrl()}?pin=${encodeURIComponent(currentPin)}`, "_blank");
  };

  let listContent;
  if (loading && items.length === 0) {
    listContent = (
      <div className="center-state">
        <div className="spinner dark" />
      </div>
    );
  } else if (items.length === 0) {
    listContent = (
      <div className="center-state">
        <Wine size={48} color="#E8DDD3" />
        <div className="empty-title">Aucune participation</div>
        <div className="empty-sub">Les entrées apparaîtront ici en temps réel.</div>
      </div>
    );
  } else {
    listContent = (
      <div className="admin-list">
        {items.map((item) => (
          <div className="participant-row" data-testid={`participant-row-${item.id}`} key={item.id}>
            <div className="avatar">
              {(item.first_name[0] || "").toUpperCase()}
              {(item.last_name[0] || "").toUpperCase()}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="row-name">{item.first_name} {item.last_name}</div>
              <div className="row-email">{item.email}</div>
              <span className={`email-badge ${item.email_sent ? "sent" : "pending"}`}>
                {item.email_sent ? "e-mail envoyé" : "envoi en attente"}
              </span>
              {item.newsletter ? (
                <span className="email-badge newsletter" data-testid={`newsletter-badge-${item.id}`}>
                  newsletter ✓
                </span>
              ) : null}
            </div>
            <span className="row-date">{formatDate(item.created_at)}</span>
          </div>
        ))}
      </div>
    );
  }

  if (!authed) {
    return (
      <div className="screen" data-testid="admin-login">
        <div style={{ position: "fixed", inset: 0, background: "#fff", zIndex: 0 }} />
        <div className="admin-screen">
          <div className="admin-header">
            <button className="icon-btn-light" data-testid="admin-back-button" onClick={() => navigate("/")}>
              <ChevronLeft size={22} />
            </button>
            <span className="admin-header-title">Espace équipe</span>
            <div style={{ width: 40 }} />
          </div>

          <div className="login-body">
            <div className="lock-icon">
              <Lock size={24} />
            </div>
            <h1 className="big-title dark">Code d&apos;accès</h1>
            <p className="subtitle" style={{ color: "#3A2A22" }}>Réservé à l&apos;équipe Cave de La Côte.</p>

            <input
              className="pin-input"
              data-testid="pin-input"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
              placeholder="••••"
              type="password"
              inputMode="numeric"
              maxLength={8}
              onKeyDown={(e) => e.key === "Enter" && onLogin()}
            />
            {err ? <div className="login-err" data-testid="login-error">{err}</div> : null}
            <button className="admin-cta" data-testid="pin-submit-button" onClick={onLogin} disabled={loading}>
              {loading ? <div className="spinner" style={{ margin: "0 auto" }} /> : "Entrer"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="screen" data-testid="admin-list">
      <div style={{ position: "fixed", inset: 0, background: "#fff", zIndex: 0 }} />
      <div className="admin-screen">
        <div className="admin-header">
          <button className="icon-btn-light" data-testid="admin-back-button" onClick={() => navigate("/")}>
            <ChevronLeft size={22} />
          </button>
          <span className="admin-header-title">Participants</span>
          <button className="icon-btn-light" data-testid="export-csv-button" onClick={onExport}>
            <Download size={20} />
          </button>
        </div>

        <div className="stat-bar">
          <span className="stat-label">TOTAL</span>
          <span className="stat-value" data-testid="total-count">{total}</span>
          <button className="refresh-btn" data-testid="refresh-button" onClick={() => refresh(currentPin)}>
            <RefreshCw size={16} />
            Rafraîchir
          </button>
        </div>

        {listContent}
      </div>
    </div>
  );
}
